<template>
  <div
    :class="modalData?.wrapperClass"
    class="dialog"
  >
    <div class="iframe-container">
      <!--
        allow="storage-access" lets the V3 app (a cross-site iframe on Vercel)
        call document.requestStorageAccess() so its auth cookie survives past the
        first request. Without it, once auth-checker strips the mfaToken from the
        URL the modal's next authenticated call 401s and V3 logs out. Matches the
        capability already granted to PreloadedV3Iframe.vue.
      -->
      <!--
        data-crm-modal is how useIframeConnection.resolveCrmIframeFor finds this
        iframe. ModalService renders this component for every legacy CRM modal,
        so the marker is the single identity of "the legacy CRM modal iframe".
      -->
      <LIframe :src="modalData.url" allow="autofocus; storage-access" crossorigin="anonymous" data-crm-modal="true" @load="handleIframeLoad" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  MessageDataObject,
  useIframeConnection,
} from '@/composables/useIframeConnection';
import {
  iframeBridge,
  isIframeModalType,
  matchesModalCloseMessage,
} from '@/composables/iframeBridgeProtocol';
import { useModalInstance } from '@/composables/useModalInstance';
import { getCurrentInstance, onMounted, ref, watch } from 'vue';
import LIframe from '@/components/LIframe.vue';

const props = defineProps<{
  modalData: {
    url: string,
    wrapperClass: string,
    name: string,
    callback?: (eventData: MessageDataObject) => void,
  }
}>();
const modal = useModalInstance<{}, string>();

const iframeLoading = ref(true);
const isFullscreen = ref(false);
const dialogElement = ref<HTMLElement | null>(null);

onMounted(() => {
  dialogElement.value = getCurrentInstance()?.proxy?.$el?.closest('.v-dialog') as HTMLElement | null;
});

watch(isFullscreen, (val) => {
  if (dialogElement.value) {
    dialogElement.value.classList.toggle('iframe-modal-fullscreen', val);
  }
});

const handleIframeLoad = (event: Event) => {
  iframeLoading.value = false;
};

const { listen } = useIframeConnection();
listen((eventData) => {
  if (iframeBridge.infra.iframeUi.fullscreenOpen.matches(eventData)) {
    isFullscreen.value = true;
    return;
  }

  if (iframeBridge.infra.iframeUi.fullscreenClose.matches(eventData)) {
    isFullscreen.value = false;
    return;
  }

  if (props.modalData.name === eventData.type) {
    if (
      isIframeModalType(props.modalData.name)
      && matchesModalCloseMessage(eventData, props.modalData.name)
    ) {
      modal.closeModal(eventData?.data);
      return;
    }

    if (props.modalData.callback) {
      props.modalData.callback(eventData);
    }
  }
});
</script>

<style scoped lang="scss">
.dialog {
  background-color: var(--token-core-surface);
  padding: 0 !important;
  
  .iframe-container {
    height: 100%;
    padding: 0;
    overflow: hidden;
    position: relative;
    width: 100%;
    z-index: 1;
  
    ::v-deep .iframe-container {
      height: 100%;
      
      iframe {
        height: 100% !important;
      }
    }
  }
  
  .dialog-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
